/**
 * Storage Service Layer using Supabase Storage
 *
 * The `uploads` bucket is PUBLIC. Uploads whose returned URL is PERSISTED into a
 * DB column (logos, profile photos, receipts, camera/time-clock photos, email
 * template imagery, chat attachments) return a PERMANENT public URL via
 * `publicUrlFor` — a signed URL there expires after SIGNED_URL_TTL_SECONDS
 * (1 hour) and 400s on every render afterwards, which is what made profile
 * photos / logos / receipts "disappear" after an hour (same class of bug fixed
 * for proposal imagery in b537e85).
 *
 * `signedUrlFor` / `getSignedUrl` are retained for callers that hold a storage
 * PATH (not a URL) and want a fresh, short-lived URL resolved on demand.
 *
 * ── RETENCIÓN: las imágenes de propuesta NO SE BORRAN. Nunca. ───────────────
 *
 * Aquí había, en tres sitios distintos, la promesa de "una futura pasada de
 * limpieza" que borrara los archivos huérfanos. Esa pasada NO se debe escribir,
 * y esta nota está donde estaba la promesa para que quien venga a cumplirla lea
 * primero por qué no.
 *
 * Una propuesta firmada es un registro de lo que alguien aceptó. Su portada y
 * sus fotos son parte de lo que el cliente vio cuando puso su nombre. Si un
 * barrido borra ese archivo tres meses después, la propuesta firmada pasa a
 * enseñar un hueco roto: se ha alterado un documento firmado, en silencio, y
 * nadie se entera hasta que un cliente lo abre para reclamar.
 *
 * Y no se ahorra nada. Una portada encogida pesa ~200 KB; cien contratistas
 * trabajando un año entero caben de sobra en la cuota, por unos dólares al mes.
 * El borrado arriesga el documento firmado para ahorrar calderilla.
 *
 * Reglas concretas:
 *  1. Quitar una foto de la biblioteca de Marketing y marca, o de la galería de
 *     una propuesta, DESVINCULA — nunca llama a `.remove()`.
 *  2. `storagePath` se guarda para identificar el objeto (depurar, medir,
 *     migrar de bucket), no para borrarlo.
 *  3. Si algún día hace falta limpiar de verdad, el filtro no es "no está en
 *     esta galería": es "ninguna propuesta, de ningún workspace, viva o
 *     firmada, apunta a este archivo" — y eso incluye las propuestas
 *     archivadas. Hasta que exista esa consulta, no hay limpieza.
 *
 * services/proposalImageRetention.test.ts falla si alguien mete un `.remove()`
 * en la ruta de imágenes de propuesta.
 *
 * (Contratos y recibos SÍ se borran — `deleteContract`, `deleteExpenseReceipt`.
 * Son otra cosa: el contrato firmado se archiva aparte por la edge function
 * archive-signed-contract antes de que su plantilla pueda desaparecer.)
 */
import { supabase } from './supabase';
import { getWorkspaceId } from './database'; // Re-use the workspace ID getter if needed
import { valeLaPenaEncoger, dimensionesDestino, CALIDAD_JPEG } from '../utils/imageResize';

const BUCKET_NAME = 'uploads';
const SIGNED_URL_TTL_SECONDS = 3600;

/** Create a signed URL for an object in the `uploads` bucket. 1-hour TTL.
 *  Throws on failure rather than silently returning a broken URL. */
async function signedUrlFor(path: string): Promise<string> {
    const { data, error } = await supabase.storage
        .from(BUCKET_NAME)
        .createSignedUrl(path, SIGNED_URL_TTL_SECONDS);
    if (error || !data?.signedUrl) {
        console.error('Storage signed-URL error:', error);
        throw error ?? new Error('Failed to create signed URL');
    }
    return data.signedUrl;
}

async function uploadFile(path: string, file: File): Promise<string> {
    const { data, error } = await supabase.storage
        .from(BUCKET_NAME)
        .upload(path, conTipoResuelto(file), { upsert: true });

    if (error) {
        console.error('Storage upload error:', error);
        throw error;
    }

    // Persisted into DB columns by callers (allowed_users.photo_url,
    // settings.profile.logoUrl, time-clock/camera/email/chat URLs) — must be the
    // permanent public URL, not a 1-hour signed one that 400s after its TTL.
    return publicUrlFor(data.path);
}

/** Resolve a fresh signed URL for a previously-uploaded path. Used by callers
 *  that hold onto a path (not URL) and need a renewed URL on demand. */
export async function getSignedUrl(path: string): Promise<string> {
    return signedUrlFor(path);
}

/** Permanent public URL for an object in the `uploads` bucket (which is public).
 *  Unlike signed URLs, these never expire — REQUIRED for proposal imagery that is
 *  saved into a proposal and must keep rendering to anonymous clients indefinitely.
 *  Using signed URLs here was the cause of proposal images "disappearing" after the
 *  1-hour TTL elapsed. */
function publicUrlFor(path: string): string {
    const { data } = supabase.storage.from(BUCKET_NAME).getPublicUrl(path);
    return data.publicUrl;
}

export async function uploadBusinessLogo(file: File): Promise<string> {
    file = await prepareImageForUpload(file);
    validateImageFile(file);
    const wsId = getWorkspaceId();
    const ext = file.name.split('.').pop() || 'png';
    const filePath = `${wsId}/settings/logo-${Date.now()}.${ext}`;
    return uploadFile(filePath, file);
}

export async function uploadUserProfilePhoto(file: File, uid: string): Promise<string> {
    file = await prepareImageForUpload(file);
    validateImageFile(file);
    const wsId = getWorkspaceId();
    const ext = file.name.split('.').pop() || 'png';
    const filePath = `${wsId}/users/${uid}/profile-${Date.now()}.${ext}`;
    return uploadFile(filePath, file);
}

// The `uploads` bucket on Supabase enforces a 10 MB cap and a MIME whitelist
// (jpeg/png/webp/gif/pdf). iPhone HEIC + iOS screenshots regularly trip both,
// and Supabase's raw error reads "InvalidRequest / mime type … is not
// supported", which is awful in a toast. Validate first and throw a message
// the UI can show verbatim.
const PROPOSAL_IMAGE_MAX_BYTES = 10 * 1024 * 1024;
const PROPOSAL_IMAGE_MIME_WHITELIST = new Set([
    'image/jpeg', 'image/png', 'image/webp', 'image/gif',
]);

/**
 * Extensión → MIME, para el archivo que el navegador no supo tipar.
 *
 * Un .HEIC de iPhone elegido en un Windows sin el códec HEIF llega con
 * `type: ''`, y por ahí se colaba entero: la validación empezaba por
 * `if (file.type && ...)`, así que sin tipo no se validaba nada y el rechazo
 * lo acababa dando el bucket con su «mime type application/octet-stream is not
 * supported» — ilegible en un toast, y encima después de subir los bytes.
 *
 * Van también los formatos que NO se aceptan (heic/heif/jp2/avif): resolverlos
 * es lo que permite contestar «conviértela a JPG» en vez de «tipo
 * desconocido».
 */
const MIME_POR_EXTENSION: Record<string, string> = {
    jpg: 'image/jpeg', jpeg: 'image/jpeg', jpe: 'image/jpeg', jfif: 'image/jpeg',
    png: 'image/png',
    webp: 'image/webp',
    gif: 'image/gif',
    heic: 'image/heic', heif: 'image/heif',
    jp2: 'image/jp2', jpx: 'image/jpx', jpf: 'image/jpx',
    avif: 'image/avif',
    pdf: 'application/pdf',
};

/** El tipo con el que se valida: el del navegador si lo hay, y si no el que
 *  delate la extensión. Cadena vacía cuando tampoco la extensión dice nada —
 *  y entonces se rechaza, que es exactamente lo contrario de lo que hacía el
 *  `file.type &&` que había en las dos validaciones de este archivo. */
function tipoResuelto(file: File): string {
    if (file.type) return file.type;
    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    return MIME_POR_EXTENSION[ext] ?? '';
}

/**
 * Re-envuelve el archivo sin tipo en un File nuevo etiquetado con lo que dice
 * su extensión. Validar no basta: un File con `type: ''` que SÍ es válido (un
 * `foto.jpg` que el navegador no supo tipar) se subiría igualmente como
 * `application/octet-stream` y lo tumbaría el bucket.
 *
 * Y no vale pasar `contentType` en las opciones: en @supabase/storage-js
 * 2.99.0 un File entra por la rama `instanceof Blob` → FormData, donde
 * `options.contentType` no se lee nunca. La etiqueta sale del propio Blob, así
 * que hay que construir uno nuevo.
 *
 * No toca el archivo que ya viene tipado, ni el de extensión desconocida (ese
 * ya lo rechazó la validación antes de llegar aquí).
 */
function conTipoResuelto(file: File): File {
    if (file.type) return file;
    const tipo = tipoResuelto(file);
    if (!tipo) return file;
    return new File([file], file.name, { type: tipo, lastModified: file.lastModified });
}

/** Expected user-input rejections (bad type / oversize file). Tagged with a
 *  distinct name so upload catch blocks can toast the friendly message WITHOUT
 *  console.error-ing an intentional validation into system_error_logs. */
function userInputError(message: string): Error {
    const e = new Error(message);
    e.name = 'UserInputError';
    return e;
}

/** Pre-validate an image upload; throws a toast-ready message. Shared by every
 *  image path (logo, profile photo, proposal imagery) so iOS HEIC/JP2 photos
 *  fail fast with a readable hint instead of Supabase's raw
 *  "mime type image/jp2 is not supported" (seen in production from the
 *  SetupWizard logo upload). */
export function validateImageFile(file: File): void {
    if (file.size > PROPOSAL_IMAGE_MAX_BYTES) {
        const mb = (file.size / (1024 * 1024)).toFixed(1);
        throw userInputError(`Image is too large (${mb} MB). Max is 10 MB — try resizing it first.`);
    }
    // Sin `file.type &&`: el archivo que el navegador no supo tipar se valida
    // por su extensión, y si tampoco la extensión lo identifica se rechaza
    // aquí. Antes ese caso —el .HEIC en un Windows sin códec— se saltaba la
    // validación entera y moría en el servidor.
    const tipo = tipoResuelto(file);
    if (!PROPOSAL_IMAGE_MIME_WHITELIST.has(tipo)) {
        const isIosFormat = /heic|heif|jp2|jpx/i.test(tipo) || /\.(heic|heif|jp2|jpx)$/i.test(file.name);
        throw userInputError(
            isIosFormat
                ? 'iPhone HEIC/JPEG-2000 photos aren\'t supported. Convert to JPG or PNG first (iOS Settings → Camera → Formats → Most Compatible).'
                : `Unsupported image type "${tipo || 'unknown'}". Use JPG, PNG, WEBP, or GIF.`
        );
    }
}

/** iPhones shoot HEIC/HEIF by default and browsers can't decode it natively
 *  (unlike AVIF). Lazy-load the wasm decoder ONLY when a HEIC/HEIF file is
 *  actually chosen — a dynamic import(), so it never touches the main bundle —
 *  and re-encode to JPEG. On ANY failure (decoder load / parse), return the
 *  original file so validateImageFile raises its friendly "convert to JPG"
 *  rejection. Oversize files are left for the size check, same as AVIF. */
async function convertHeicToJpeg(file: File): Promise<File> {
    const isHeic = /image\/(heic|heif)/i.test(file.type) || /\.(heic|heif)$/i.test(file.name);
    if (!isHeic) return file;
    if (file.size > PROPOSAL_IMAGE_MAX_BYTES) return file; // oversize rejection applies first
    try {
        const { default: heic2any } = await import('heic2any');
        const out = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.9 });
        const blob = Array.isArray(out) ? out[0] : out;
        if (!blob) return file;
        const jpgName = file.name.replace(/\.(heic|heif)$/i, '') + '.jpg';
        return new File([blob], jpgName, { type: 'image/jpeg' });
    } catch {
        // Decoder unavailable or the file couldn't be parsed — fall back to the
        // friendly whitelist rejection in validateImageFile.
        return file;
    }
}

/** Normalize a modern/iPhone image format the uploads bucket can't accept into
 *  JPEG before upload: HEIC/HEIF via the lazy wasm decoder, AVIF via canvas.
 *  Each converter is a no-op for non-matching types and falls back to the
 *  original file on failure. Run at the top of every image upload path. */
async function prepareImageForUpload(file: File): Promise<File> {
    file = await convertHeicToJpeg(file);
    file = await convertAvifToJpeg(file);
    return file;
}

/** AVIF is decodable by modern Chrome/Safari but the uploads bucket whitelist
 *  has no image/avif — instead of rejecting, transparently re-encode to JPEG
 *  client-side. Returns every other type (and oversize files, which fail the
 *  size check anyway) untouched. If the browser can't decode AVIF, returns the
 *  original file so validateImageFile raises its friendly whitelist rejection.
 *  Note the size limit is re-checked AFTER conversion — callers run
 *  validateImageFile on the returned file. */
async function convertAvifToJpeg(file: File): Promise<File> {
    if (file.type !== 'image/avif') return file;
    if (file.size > PROPOSAL_IMAGE_MAX_BYTES) return file; // skip decode; size rejection applies
    try {
        const bitmap = await createImageBitmap(file);
        const canvas = document.createElement('canvas');
        canvas.width = bitmap.width;
        canvas.height = bitmap.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) return file;
        // JPEG has no alpha channel — flatten transparency onto white (canvas
        // default would render it black, e.g. transparent-background logos).
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(bitmap, 0, 0);
        bitmap.close();
        const blob = await new Promise<Blob | null>(resolve =>
            canvas.toBlob(resolve, 'image/jpeg', 0.9)
        );
        if (!blob) return file;
        const jpgName = file.name.replace(/\.avif$/i, '') + '.jpg';
        return new File([blob], jpgName, { type: 'image/jpeg' });
    } catch {
        // Browser can't decode AVIF (e.g. older Safari) — fall back to the
        // friendly rejection in validateImageFile.
        return file;
    }
}

/**
 * Encoge una foto a `ANCHO_MAXIMO` antes de subirla, re-codificando a JPEG.
 *
 * Solo para imágenes de propuesta. NO se aplica a logos ni a fotos de perfil:
 * un logo suele ser un PNG con transparencia y aplanarlo contra blanco lo
 * arruina en cuanto cae sobre una cabecera de color.
 *
 * Devuelve el archivo original ante CUALQUIER problema —decodificador,
 * canvas, o un resultado que no pesa menos— porque encoger es una mejora, no un
 * requisito: entre subir la foto pesada y no subir nada, se sube la pesada.
 */
async function downscaleProposalImage(file: File): Promise<File> {
    // GIF ni se decodifica: sea cual sea su tamaño, re-codificarlo lo estropea.
    if (file.type === 'image/gif') return file;
    // Techo de cordura antes de decodificar. `createImageBitmap` de un archivo
    // enorme reserva ancho×alto×4 bytes de golpe y tumba la pestaña en un
    // teléfono. Por encima de esto se deja pasar y lo rechaza validateImageFile
    // con su mensaje de "demasiado grande".
    if (file.size > 40 * 1024 * 1024) return file;
    try {
        const bitmap = await createImageBitmap(file);
        if (!valeLaPenaEncoger(file.type, file.size, bitmap.width)) {
            bitmap.close();
            return file;
        }
        const { ancho, alto } = dimensionesDestino(bitmap.width, bitmap.height);
        if (!ancho || !alto) { bitmap.close(); return file; }

        const canvas = document.createElement('canvas');
        canvas.width = ancho;
        canvas.height = alto;
        const ctx = canvas.getContext('2d');
        if (!ctx) { bitmap.close(); return file; }
        // JPEG no tiene canal alfa — se aplana sobre blanco, que si no el canvas
        // pinta la transparencia en negro.
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, ancho, alto);
        ctx.drawImage(bitmap, 0, 0, ancho, alto);
        bitmap.close();

        const blob = await new Promise<Blob | null>(resolve =>
            canvas.toBlob(resolve, 'image/jpeg', CALIDAD_JPEG)
        );
        // Nunca empeorar: una foto ya optimizada puede salir MÁS grande al
        // re-codificarla, y en ese caso el original es la mejor versión.
        if (!blob || blob.size >= file.size) return file;

        const nombre = file.name.replace(/\.[^.]+$/, '') + '.jpg';
        return new File([blob], nombre, { type: 'image/jpeg' });
    } catch {
        return file;
    }
}

/**
 * Upload a proposal cover/gallery image. Returns a PERMANENT PUBLIC URL plus the
 * storage path. Proposal images must outlive a 1-hour signed-URL TTL — they are
 * saved into the proposal and rendered to anonymous clients long after upload —
 * so this path uses the public URL rather than `signedUrlFor`.
 *
 * ⚠️ ESTOS ARCHIVOS NO SE BORRAN NUNCA. Ver la nota de retención al final de
 * este archivo antes de escribir cualquier limpieza de bucket.
 *
 * `path` se devuelve para poder identificar el objeto (depurar, medir, migrar),
 * NO para borrarlo.
 */
export async function uploadProposalImage(file: File, _path?: string): Promise<{ url: string; path: string }> {
    file = await prepareImageForUpload(file);
    // Encoger va DESPUÉS de convertir (el HEIC ya es JPEG aquí) y ANTES de
    // validar: una foto de 12 MB que se queda en 300 KB al encogerla ya no
    // choca con el tope de 10 MB, así que deja de ser un rechazo.
    file = await downscaleProposalImage(file);
    validateImageFile(file);
    const wsId = getWorkspaceId();
    const ext = file.name.split('.').pop() || 'png';
    const filePath = `${wsId}/proposals/img-${Date.now()}.${ext}`;
    const { data, error } = await supabase.storage
        .from(BUCKET_NAME)
        .upload(filePath, conTipoResuelto(file), { upsert: true });
    if (error) {
        console.error('Storage upload error:', error);
        throw error;
    }
    return { url: publicUrlFor(data.path), path: data.path };
}

export async function uploadTimeClockPhoto(file: File): Promise<string> {
    // Mismo trato que logo/perfil, que hasta ahora no tenía: el HEIC del
    // iPhone se convierte, y lo que no se pueda convertir se rechaza con el
    // mensaje de siempre en vez de con el error crudo del bucket.
    file = await prepareImageForUpload(file);
    validateImageFile(file);
    const wsId = getWorkspaceId();
    const ext = file.name.split('.').pop() || 'png';
    const filePath = `${wsId}/timeclock/photo-${Date.now()}.${ext}`;
    return uploadFile(filePath, file);
}

export async function uploadCompanyCameraPhoto(projectId: string, file: File): Promise<string> {
    file = await prepareImageForUpload(file);
    validateImageFile(file);
    const wsId = getWorkspaceId();
    const ext = file.name.split('.').pop() || 'png';
    const filePath = `${wsId}/projects/${projectId}/camera-${Date.now()}.${ext}`;
    return uploadFile(filePath, file);
}

/**
 * Upload a receipt photo for a project expense.
 * Returns the public URL + the storage path (used later to delete).
 */
export async function uploadExpenseReceipt(
    projectId: string,
    expenseId: string,
    file: File
): Promise<{ url: string; path: string }> {
    // El recibo sale de la cámara del teléfono como cualquier otra foto.
    file = await prepareImageForUpload(file);
    validateImageFile(file);
    const wsId = getWorkspaceId();
    const ext = file.name.split('.').pop() || 'jpg';
    const filePath = `${wsId}/projects/${projectId}/expenses/${expenseId}/receipt-${Date.now()}.${ext}`;

    const { data, error } = await supabase.storage
        .from(BUCKET_NAME)
        .upload(filePath, conTipoResuelto(file), { upsert: true });
    if (error) throw error;

    // Permanent public URL — the receipt URL is persisted on the expense row and
    // re-rendered long after upload; a signed URL 400s once its 1-hour TTL lapses.
    const url = publicUrlFor(data.path);
    return { url, path: data.path };
}

/** Delete a receipt photo from storage. Silent on failure since the row write also clears it. */
export async function deleteExpenseReceipt(path: string): Promise<void> {
    if (!path) return;
    const { error } = await supabase.storage.from(BUCKET_NAME).remove([path]);
    if (error) {
        console.warn('[deleteExpenseReceipt] Failed to remove file:', error.message);
    }
}

/**
 * Upload a contract PDF stored in the tenant's Settings → Contracts library.
 * Returns the public URL + storage path so we can later delete the file when
 * the user removes the contract from settings.
 */
// Must match the uploads bucket file_size_limit (10485760) — same cap as
// ContractsSection's MAX_BYTES pre-check.
const CONTRACT_MAX_BYTES = 10 * 1024 * 1024;

export async function uploadContract(file: File): Promise<{ url: string; path: string }> {
    // Defense-in-depth mirror of the ContractsSection pre-checks: the bucket
    // enforces the 10 MiB cap + MIME whitelist server-side, and Supabase's raw
    // "The object exceeded the maximum allowed size" is awful in a toast
    // (seen in production from 10-25 MB PDFs).
    if (file.size > CONTRACT_MAX_BYTES) {
        const mb = (file.size / (1024 * 1024)).toFixed(1);
        throw userInputError(`File is ${mb} MB — contracts are capped at 10 MB. Please compress the PDF and try again.`);
    }
    // Sin `file.type &&`, por lo mismo que en validateImageFile: un archivo sin
    // tipo se juzga por su extensión, y un `.HEIC` sin tipar no es un PDF.
    if (tipoResuelto(file) !== 'application/pdf') {
        throw userInputError('Only PDF files are supported.');
    }
    const wsId = getWorkspaceId();
    const safeName = file.name
        .replace(/\s+/g, '-')
        .replace(/[^a-zA-Z0-9._-]/g, '')
        .slice(0, 80) || 'contract.pdf';
    const filePath = `${wsId}/contracts/${Date.now()}-${safeName}`;
    const { data, error } = await supabase.storage
        .from(BUCKET_NAME)
        .upload(filePath, conTipoResuelto(file), { upsert: false });
    if (error) throw error;
    // Permanent public URL — this url is PERSISTED on ContractFile and later
    // fetched by the field editor, sign flows, and anonymous proposal viewers.
    // A signed URL here expires after 1 hour and 400s forever after (the
    // "Unexpected server response (400) while retrieving PDF" production error).
    const url = publicUrlFor(data.path);
    return { url, path: data.path };
}

/** Delete a contract PDF from storage. Silent on failure since the settings
 *  row write also clears the metadata pointer. */
export async function deleteContract(path: string): Promise<void> {
    if (!path) return;
    const { error } = await supabase.storage.from(BUCKET_NAME).remove([path]);
    if (error) console.warn('[deleteContract] Failed to remove file:', error.message);
}

/**
 * Sube UNA hoja del contrato ya rasterizada (services/contractPrintPages).
 *
 * `upsert: true` y un nombre estable por hoja: rehacer las hojas —al
 * contrafirmar, o al mover un campo— pisa las anteriores en vez de dejar un
 * rastro de versiones muertas en el bucket. El número de hoja va con ceros a la
 * izquierda para que el listado del bucket salga en orden.
 */
export async function uploadContractPage(
    blob: Blob,
    contractId: string,
    pageNumber: number,
): Promise<{ url: string; path: string }> {
    const wsId = getWorkspaceId();
    const safeId = contractId.replace(/[^a-zA-Z0-9._-]/g, '') || 'contract';
    const filePath = `${wsId}/contracts/print/${safeId}/p${String(pageNumber).padStart(3, '0')}.png`;
    const { data, error } = await supabase.storage
        .from(BUCKET_NAME)
        .upload(filePath, blob, { upsert: true, contentType: 'image/png' });
    if (error) throw error;
    // Pública y permanente: estas hojas acaban dentro del PDF que abre un
    // cliente anónimo desde el enlace público, donde una URL firmada moriría.
    return { url: publicUrlFor(data.path), path: data.path };
}

/** Borra las hojas rasterizadas de un contrato. Silencioso: dejar un PNG
 *  huérfano es mucho menos grave que impedir borrar el contrato. */
/**
 * Borra las páginas rasterizadas de un contrato.
 *
 * Recibe rutas ya armadas, así que un llamador equivocado podría pasarle la
 * portada de una propuesta firmada y este borraría sin chistar. Por eso solo
 * acepta lo que vive bajo `contracts/print/`: esas páginas son un derivado que
 * se puede volver a generar, y las imágenes de propuesta no se borran nunca
 * (ver `proposalImageRetention.test.ts`).
 */
export async function deleteContractPages(paths: string[]): Promise<void> {
    const limpios = paths.filter(Boolean);
    const ajenos = limpios.filter(p => !p.includes('/contracts/print/'));
    if (ajenos.length > 0) {
        console.warn('[deleteContractPages] Ignoradas rutas fuera de contracts/print/:', ajenos);
    }
    const borrables = limpios.filter(p => p.includes('/contracts/print/'));
    if (borrables.length === 0) return;
    const { error } = await supabase.storage.from(BUCKET_NAME).remove(borrables);
    if (error) console.warn('[deleteContractPages] Failed to remove files:', error.message);
}

/**
 * Upload a chat attachment (image or PDF) that the user drops into the AI chat.
 * Path is workspace + conversation scoped so we can garbage-collect old conversations later.
 * File names are sanitized to strip characters that break URLs or storage paths.
 */
export async function uploadChatAttachment(file: File, conversationId: string): Promise<string> {
    const wsId = getWorkspaceId();
    const safeName = file.name
        .replace(/\s+/g, '-')
        .replace(/[^a-zA-Z0-9._-]/g, '')
        .slice(0, 80) || 'file';
    const filePath = `${wsId}/ai-chat/${conversationId || 'scratch'}/${Date.now()}-${safeName}`;
    return uploadFile(filePath, file);
}

/**
 * Upload a voice enrollment sample (60s webm from the Settings → AI Voice flow).
 * Stored per-user so we can re-clone with the same audio if we swap voice providers
 * or ElevenLabs ships a better model.
 */
export async function uploadVoiceSample(file: File | Blob, userId: string): Promise<string> {
    const wsId = getWorkspaceId();
    const wrapped = file instanceof File
        ? file
        : new File([file], `sample-${Date.now()}.webm`, { type: (file as Blob).type || 'audio/webm' });
    const ext = (wrapped.name.split('.').pop() || 'webm').toLowerCase();
    const filePath = `${wsId}/voice-samples/${userId}/sample-${Date.now()}.${ext}`;
    return uploadFile(filePath, wrapped);
}
